import { useCallback, useRef, useState } from 'react';

import type { BizyHttpClient } from '../client';
import type { BizyChatMessage } from '../types/chat';
import type { BizyUserMessage } from '../types/conversations';
import { hydrateMessages } from './messages';

// ─── API contract ────────────────────────────────────────────────────────────

const TIMELINE_PATH = '/bizy/chat';
const CURRENT_SEGMENT_PATH = '/bizy/chat/current_segment';
const DEFAULT_LIMIT = 30;

export interface BizyCurrentSegmentResponse {
	conversationId: string;
	isNewSegment: boolean;
}

interface BizyTimelineApiResponse {
	turns: BizyUserMessage[];
	hasMore: boolean;
	nextCursor: string | null;
}

// ─── Public types ────────────────────────────────────────────────────────────

export interface UseBizyTimelineOptions {
	/** Transport client (same one passed to useBizyChatState). */
	http: BizyHttpClient;
	/** Number of turns to load per page. Defaults to 30, capped at 50 by the server. */
	limit?: number;
}

export interface UseBizyTimelineResult {
	/** True while the initial newest-page fetch is in flight. */
	isLoadingNewest: boolean;
	/** True while an older-page fetch (infinite-scroll upward) is in flight. */
	isLoadingOlder: boolean;
	/** Whether there are older turns to load. Starts true; set false when the server returns has_more=false. */
	hasMore: boolean;
	/**
	 * Fetch the current active segment id from the server. Call once on init;
	 * returns the id to seed the chat connector's conversationId.
	 */
	fetchCurrentSegment: () => Promise<BizyCurrentSegmentResponse>;
	/**
	 * Load the newest page of the timeline. Returns hydrated messages
	 * (oldest→newest within the page) and records the cursor for the next
	 * older-page call. Call once on init after fetchCurrentSegment.
	 */
	loadNewest: () => Promise<BizyChatMessage[]>;
	/**
	 * Load the next older page (infinite-scroll upward). Returns hydrated
	 * messages in chronological order (oldest first within the page), suitable
	 * for prepending to the connector via prependMessages. Returns an empty
	 * array and is a no-op when hasMore is false.
	 */
	loadOlder: () => Promise<BizyChatMessage[]>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Headless pager for the Bizy continuous-chat timeline.
 *
 * Manages cursor state and provides three operations for the host to wire:
 *   1. fetchCurrentSegment — get the active segment id on init
 *   2. loadNewest — initial history load (newest page)
 *   3. loadOlder — infinite-scroll upward (older pages)
 *
 * No react-query. No rendering. Transport-agnostic (uses the injected BizyHttpClient).
 */
export function useBizyTimeline({ http, limit = DEFAULT_LIMIT }: UseBizyTimelineOptions): UseBizyTimelineResult {
	const [isLoadingNewest, setIsLoadingNewest] = useState(false);
	const [isLoadingOlder, setIsLoadingOlder] = useState(false);
	const [hasMore, setHasMore] = useState(true);

	// The cursor for the next older-page fetch. Null = haven't loaded the first page yet.
	const nextCursorRef = useRef<string | null>(null);
	// Ref-based guards prevent duplicate in-flight requests when rapid scroll events
	// fire before React state updates propagate.
	const isLoadingOlderRef = useRef(false);
	const hasMoreRef = useRef(true);

	const fetchCurrentSegment = useCallback(async (): Promise<BizyCurrentSegmentResponse> => {
		return http.get<BizyCurrentSegmentResponse>(CURRENT_SEGMENT_PATH, { headers: { 'Key-Inflection': 'camel' } });
	}, [http]);

	const loadNewest = useCallback(async (): Promise<BizyChatMessage[]> => {
		setIsLoadingNewest(true);
		try {
			const data = await http.get<BizyTimelineApiResponse>(TIMELINE_PATH, {
				params: { limit },
				headers: { 'Key-Inflection': 'camel' },
			});
			nextCursorRef.current = data.nextCursor;
			hasMoreRef.current = data.hasMore;
			setHasMore(data.hasMore);
			return hydrateMessages(data.turns);
		} finally {
			setIsLoadingNewest(false);
		}
	}, [http, limit]);

	const loadOlder = useCallback(async (): Promise<BizyChatMessage[]> => {
		if (!hasMoreRef.current || !nextCursorRef.current || isLoadingOlderRef.current) return [];
		isLoadingOlderRef.current = true;
		setIsLoadingOlder(true);
		try {
			const data = await http.get<BizyTimelineApiResponse>(TIMELINE_PATH, {
				params: { limit, before: nextCursorRef.current },
				headers: { 'Key-Inflection': 'camel' },
			});
			nextCursorRef.current = data.nextCursor;
			hasMoreRef.current = data.hasMore;
			setHasMore(data.hasMore);
			return hydrateMessages(data.turns);
		} finally {
			isLoadingOlderRef.current = false;
			setIsLoadingOlder(false);
		}
	}, [http, limit]);

	return { isLoadingNewest, isLoadingOlder, hasMore, fetchCurrentSegment, loadNewest, loadOlder };
}
